import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";

import { mdiCopyright, mdiLink, mdiShareVariant, mdiWeb } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import Disposition, { cloneDispositionState } from "./Disposition";
import { setProject } from "../../../../actions/architecture";
import ProjectAPI from "../../../../api/ProjectAPI";
import Block from "../../../../components/block/Block";
import Input from "../../../../components/form/Input";
import { DRAG_TYPE } from "../../../../constants/dragTypes";
import useSaveHeaderFooterComponentListMutation from "../../../../hooks/mutations/useSaveHeaderFooterComponentListMutation";
import { headerFooterComponentListApiToDispositionState } from "../../../../utils/components";

import type { JSX, Ref } from "react";

import type { DispositionState, ItemDisposition } from "./Disposition";
import type {
  FooterComponent,
  Project,
} from "../../../../api/ProjectAPI.types";
import type { AppState } from "../../../../reducers";

export interface FooterDispositionRef {
  onSave: () => Promise<void>;
}

interface FormValues {
  isStickyFooter?: boolean;
}

function FooterDisposition(
  _: unknown,
  ref: Ref<FooterDispositionRef>,
): JSX.Element {
  const { projectSlug } = useParams<{
    projectSlug: string;
  }>();
  const { t } = useTranslation("editor-platform");
  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const saveComponentListMutation = useSaveHeaderFooterComponentListMutation();

  const footerElements: ItemDisposition[] = useMemo(
    () => [
      {
        id: "text",
        label: t("copyright"),
        icon: <Icon path={mdiCopyright} size={0.9375} />,
        rank: 1,
      },
      {
        id: "page_links",
        label: t("pageLinks"),
        icon: <Icon path={mdiLink} size={0.9375} />,
        rank: 2,
      },
      {
        id: "lang",
        label: t("languages"),
        icon: <Icon path={mdiWeb} size={0.9375} />,
        rank: 3,
      },
      {
        id: "social_links",
        label: t("socialLinks"),
        icon: <Icon path={mdiShareVariant} size={0.9375} />,
        rank: 4,
      },
    ],
    [t],
  );

  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );

  const getComponentsQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getComponentList<FooterComponent>,
    params: [
      {
        projectSlug,
        sectionList: ["footer"],
      },
    ],
  });

  const { data: footerComponents } = useQuery(getComponentsQuery);

  const updateProjectMutation = useMutation({
    mutationFn: async (data: Project) =>
      ProjectAPI.updateProject({
        projectSlug,
        data,
      }),
    onMutate: (data) => {
      const previousProject = queryClient.getQueryData([
        "getProject",
        projectSlug,
      ]);
      queryClient.setQueryData(["getProject", projectSlug], data);
      return { previousProject, project: data };
    },
    onSuccess: (__, ___, context) => {
      if (context?.project) {
        dispatch(setProject({ project: context?.project, projectSlug }));
      }
      toast.success(t("editor-notif:saveSuccess"), {
        id: "save-footer-disposition-success",
      });
    },
    onError: (_error, _variables, context) => {
      toast.error(t("editor-notif:saveError"), {
        id: "save-footer-disposition-error",
      });
      if (context?.previousProject) {
        queryClient.setQueryData(
          ["getProject", projectSlug],
          context.previousProject,
        );
      }
    },
  });

  const [dispositionState, setDispositionState] =
    React.useState<DispositionState>({
      available: [],
      left: [],
      center: [],
      right: [],
    });
  const [prevDispositionState, setPrevDispositionState] =
    React.useState<DispositionState>({
      available: [],
      left: [],
      center: [],
      right: [],
    });

  const methods = useForm<FormValues>({
    values: {
      isStickyFooter: project?.storefront_project_params.allow_sticky_footer,
    },
  });

  useImperativeHandle<FooterDispositionRef, FooterDispositionRef>(
    ref,
    () => ({
      onSave: async (): Promise<void> => {
        await saveComponentListMutation.mutateAsync({
          dispositionState,
          prevDispositionState,
          section: "footer",
          onSuccess: (): void => {
            queryClient.invalidateQueries(getComponentsQuery.queryKey);
          },
        });

        const isStickyFooter = methods.getValues("isStickyFooter");

        if (
          project &&
          project.storefront_project_params.allow_sticky_footer !==
            isStickyFooter
        ) {
          try {
            await updateProjectMutation.mutateAsync({
              ...project,
              storefront_project_params: {
                ...project?.storefront_project_params,
                allow_sticky_footer: Boolean(isStickyFooter),
              },
            });
          } catch (error) {
            console.error("Error updating sticky footer:", error);
          }
        }
      },
    }),
    [
      saveComponentListMutation,
      dispositionState,
      prevDispositionState,
      project,
      queryClient,
      getComponentsQuery.queryKey,
      methods,
      updateProjectMutation,
    ],
  );

  useEffect(() => {
    if (footerComponents) {
      const newState = headerFooterComponentListApiToDispositionState(
        footerComponents.data,
        footerElements,
      );
      setDispositionState(newState);
      setPrevDispositionState(cloneDispositionState(newState));
    }
  }, [footerComponents, footerElements]);

  return (
    <Block title={t("footer")}>
      <div className="flex flex-col gap-[1.875rem]">
        <Disposition
          acceptItemType={DRAG_TYPE.FOOTER_COMPONENT_ITEM}
          dispositionState={dispositionState}
          setDispositionState={setDispositionState}
        />
        <FormProvider
          {...methods} // eslint-disable-line react/jsx-props-no-spreading
        >
          <Input
            isCompact
            isReversed
            label={t("allow_sticky_footer")}
            name="isStickyFooter"
            size="sm"
            type="switch"
          />
        </FormProvider>
      </div>
    </Block>
  );
}

export default forwardRef(FooterDisposition);
